import { FC } from 'react';
import { useTypedDispatch, useTypedSelector } from '@/store/hooks';
import {
	ActionIcon,
	Box,
	Button,
	Divider,
	Drawer,
	Flex,
	LoadingOverlay,
	Text,
} from '@mantine/core';
import { creatorActions } from '@/store/slices/creatorSlice';
import { ProjectRenderer } from '@/renderer/ProjectRenderer';
import { ExportType } from '@/renderer/Exporter';
import { useMutation } from '@tanstack/react-query';
import { formatExactDate } from '@/utils/utilities';
import ArrowLeftIcon from '@/assets/icons/arrowLeft.svg?react';
import ModelIcon from '@/assets/icons/blocks.svg?react';
import PdfIcon from '@/assets/icons/file-type-pdf.svg?react';
import ExcelIcon from '@/assets/icons/file-spreadsheet.svg?react';
import download from 'js-file-download';
import ProjectApi from '@/api/ProjectApi';
import AppApi from '@/api/AppApi';
import { useContent } from '@/providers/ContentProvider';

const renderer = ProjectRenderer.getInstance();

const baseSlug = 'downloadDrawer.';

export const DownloadDrawer: FC = () => {
	const dispatch = useTypedDispatch();

	const { getContent } = useContent();

	const { options, project } = useTypedSelector((state) => state.creator);

	const handleClose = () => {
		dispatch(creatorActions.updateOptions({ downloadDrawerOpened: false }));
	};

	const handleDownloadCollection = () => {
		renderer.handleExportCollection(
			ExportType.GLTF,
			getContent(`${baseSlug}gltfFilename`) as string
		);
	};

	const saveProjectImage = async () => {
		const blob = await renderer.screenshot();
		const attachment = await AppApi.createAttachment({ file: blob });

		await ProjectApi.update({
			id: project.id,
			image: attachment,
		});
	};

	const handleDownloadPDF = async () => {
		await saveProjectImage();

		const pdfBlob = await ProjectApi.exportFile({
			id: project.id,
			type: 'pdf',
		});

		return pdfBlob;
	};

	const getPDFMutation = useMutation({
		mutationFn: handleDownloadPDF,
		onSuccess: (blob) => {
			download(blob, getContent(`${baseSlug}pdfFilename`) as string);
		},
	});

	const handleDownloadXLSX = async () => {
		await saveProjectImage();

		const xlsxBlob = await ProjectApi.exportFile({
			id: project.id,
			type: 'xlsx',
		});

		return xlsxBlob;
	};

	const getXLSXMutation = useMutation({
		mutationFn: handleDownloadXLSX,
		onSuccess: (blob) => {
			download(
				blob,
				getContent(`${baseSlug}xlsxFilename`, {
					vars: {
						$date: formatExactDate(new Date()),
					},
				}) as string
			);
		},
	});

	const handleDownload = (type: 'pdf' | 'xlsx') => () => {
		switch (type) {
			case 'pdf':
				getPDFMutation.mutate();
				break;
			case 'xlsx':
				getXLSXMutation.mutate();
				break;

			default:
				throw new Error('INVALID_EXPORT_TYPE');
		}
	};

	return (
		<Drawer
			opened={options.downloadDrawerOpened}
			onClose={handleClose}
			title={
				<Flex gap="sm" align="center">
					<ActionIcon onClick={handleClose} variant="transparent">
						<ArrowLeftIcon />
					</ActionIcon>
					<Text size="lg" fw="bold">
						{getContent(`${baseSlug}title`)}
					</Text>
				</Flex>
			}
		>
			<Box pos="relative">
				<LoadingOverlay
					visible={getPDFMutation.isPending || getXLSXMutation.isPending}
				/>
				<Divider my="md" />
				<Flex gap="md" direction="column">
					<Text size="sm" fw="bold" mb="xs">
						{getContent(`${baseSlug}question`)}
					</Text>
					<Button
						color="black"
						size="md"
						leftSection={<ModelIcon stroke="white" />}
						fullWidth
						onClick={handleDownloadCollection}
					>
						{getContent(`${baseSlug}download3d`)}
					</Button>
					<Button
						color="black"
						size="md"
						leftSection={<PdfIcon stroke="white" />}
						fullWidth
						onClick={handleDownload('pdf')}
					>
						{getContent(`${baseSlug}downloadPdf`)}
					</Button>
					<Button
						color="black"
						size="md"
						leftSection={<ExcelIcon stroke="white" />}
						fullWidth
						onClick={handleDownload('xlsx')}
					>
						{getContent(`${baseSlug}downloadExcel`)}
					</Button>
				</Flex>
			</Box>
		</Drawer>
	);
};
